import { useEffect, useCallback, useState } from 'react';
import { registerDeviceToken } from './useDeviceRegistration';

function detectNativeApp(): boolean {
  if (typeof window === 'undefined') return false;
  return Boolean(window.ReactNativeWebView) || navigator.userAgent.includes('MiloraApp');
}

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (data: string) => void;
    };
  }
}

interface RNMessage {
  type: string;
  [key: string]: unknown;
}

export type { RNMessage };

type MessageHandler = (data: RNMessage) => void;

import type { NativeNotificationPermissionStatus } from '@/lib/native-notification-permission';
import { parseNativeNotificationPermissionStatus } from '@/lib/native-notification-permission';
import { detectNativeDeviceType, setNativeAppPlatform } from '@/lib/native-device-type';

interface ReactNativeMessageHandlerOptions {
  onFcmToken?: (token: string) => void | Promise<void>;
  onFcmTokenError?: (message: string) => void;
  onNotificationPermissionStatus?: (status: NativeNotificationPermissionStatus) => void;
  handlers?: Record<string, MessageHandler>;
}

/**
 * Hook to handle messages from React Native WebView.
 * Provides methods to communicate back to the native app.
 */
export function useReactNativeBridge() {
  const [polledNative, setPolledNative] = useState(false);

  useEffect(() => {
    if (detectNativeApp()) return;

    let attempts = 0;
    const interval = window.setInterval(() => {
      attempts += 1;
      if (detectNativeApp()) {
        setPolledNative(true);
        window.clearInterval(interval);
      } else if (attempts >= 20) {
        window.clearInterval(interval);
      }
    }, 100);

    return () => window.clearInterval(interval);
  }, []);

  const isNativeApp = detectNativeApp() || polledNative;

  const sendMessage = useCallback((data: Record<string, unknown>) => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify(data));
    }
  }, []);

  const requestNotificationPermission = useCallback(() => {
    sendMessage({ type: 'REQUEST_NOTIFICATION_PERMISSION' });
  }, [sendMessage]);

  const queryNotificationPermissionStatus = useCallback(() => {
    sendMessage({ type: 'GET_NOTIFICATION_PERMISSION_STATUS' });
  }, [sendMessage]);

  const requestFcmToken = useCallback(() => {
    sendMessage({ type: 'REQUEST_FCM_TOKEN' });
  }, [sendMessage]);

  const openNotificationSettings = useCallback(() => {
    sendMessage({ type: 'OPEN_NOTIFICATION_SETTINGS' });
  }, [sendMessage]);

  const openQRScanner = useCallback(() => {
    sendMessage({ type: 'OPEN_QR_SCANNER' });
  }, [sendMessage]);

  const openExternalUrl = useCallback(
    (url: string) => {
      sendMessage({ type: 'OPEN_EXTERNAL_URL', url });
    },
    [sendMessage]
  );

  const requestDeviceContacts = useCallback(
    (requestId: string) => {
      sendMessage({ type: 'REQUEST_DEVICE_CONTACTS', requestId });
    },
    [sendMessage]
  );

  const openAppSettings = useCallback(() => {
    sendMessage({ type: 'OPEN_APP_SETTINGS' });
  }, [sendMessage]);

  const requestNativeAppInfo = useCallback(() => {
    sendMessage({ type: 'REQUEST_NATIVE_APP_INFO' });
  }, [sendMessage]);

  const sendToNative = useCallback(
    (type: string, data?: Record<string, unknown>) => {
      sendMessage({ type, ...data });
    },
    [sendMessage]
  );

  return {
    sendMessage,
    sendToNative,
    requestNotificationPermission,
    queryNotificationPermissionStatus,
    requestFcmToken,
    openNotificationSettings,
    openQRScanner,
    openExternalUrl,
    requestDeviceContacts,
    openAppSettings,
    requestNativeAppInfo,
    isNativeApp,
  };
}

/**
 * Hook to listen for React Native messages and handle them.
 * Automatically registers FCM tokens with the API.
 */
export function useReactNativeMessageHandler(options?: ReactNativeMessageHandlerOptions) {
  const onFcmToken = options?.onFcmToken;
  const onFcmTokenError = options?.onFcmTokenError;
  const onNotificationPermissionStatus = options?.onNotificationPermissionStatus;
  const handlers = options?.handlers;

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        const data: RNMessage = JSON.parse(event.data);
        console.log('[RN Bridge] Message received:', data.type);

        switch (data.type) {
          case 'NATIVE_APP_INFO':
            setNativeAppPlatform(data.platform);
            handlers?.['NATIVE_APP_INFO']?.(data);
            break;

          case 'FCM_TOKEN':
            console.log('[FCM] Received FCM_TOKEN from React Native:', {
              hasToken: !!data.token,
              tokenLength: typeof data.token === 'string' ? data.token.length : 0,
              tokenPreview:
                typeof data.token === 'string' ? data.token.substring(0, 30) + '...' : null,
              refreshed: data.refreshed || false,
            });

            if (data.token && typeof data.token === 'string') {
              if (onFcmToken) {
                void onFcmToken(data.token);
              } else {
                registerDeviceToken(data.token, detectNativeDeviceType())
                  .then(() => console.log('[FCM] ✓ Token registered successfully'))
                  .catch((err) => console.error('[FCM] ✗ Failed to register token:', err.message));
              }
            } else {
              console.warn('[FCM] Received FCM_TOKEN but token is empty or invalid');
            }
            break;

          case 'FCM_TOKEN_ERROR': {
            const message =
              typeof data.message === 'string' ? data.message : 'Failed to obtain FCM token';
            console.error('[FCM] Native token error:', message);
            onFcmTokenError?.(message);
            handlers?.['FCM_TOKEN_ERROR']?.(data);
            break;
          }

          case 'NOTIFICATION_PERMISSION_STATUS': {
            const status = parseNativeNotificationPermissionStatus(data.status);
            if (status) {
              onNotificationPermissionStatus?.(status);
            }
            break;
          }

          case 'NOTIFICATION_RECEIVED':
            handlers?.['NOTIFICATION_RECEIVED']?.(data);
            break;

          case 'QR_RESULT':
            handlers?.['QR_RESULT']?.(data);
            break;

          default:
            handlers?.[data.type]?.(data);
        }
      } catch (_e) {
        // Not a JSON message or not from RN, ignore
      }
    };

    window.addEventListener('message', handleMessage);

    document.addEventListener('message' as keyof DocumentEventMap, handleMessage as EventListener);

    return () => {
      window.removeEventListener('message', handleMessage);
      document.removeEventListener(
        'message' as keyof DocumentEventMap,
        handleMessage as EventListener
      );
    };
  }, [handlers, onFcmToken, onFcmTokenError, onNotificationPermissionStatus]);
}
